/**
 * Cryptographically secure randomness for the draw.
 *
 * Every draw-critical decision (which ticket wins, which gender track is next)
 * must come from here — never Math.random, and never a plain modulo of a random
 * word, which skews the distribution toward the low end of the range.
 */

/** Uniform integer in [min, max], inclusive. Rejection-sampled, so unbiased. */
export const cryptoRandomInt = (min, max) => {
    const range = max - min + 1;
    if (range <= 1) return min;

    const bytesNeeded = Math.ceil(Math.log2(range) / 8);
    const maxValue = Math.pow(256, bytesNeeded);
    // Discard the ragged tail above the last whole multiple of `range`; without
    // this, the first `maxValue % range` values would come up slightly more often.
    const limit = maxValue - (maxValue % range);
    const bytes = new Uint8Array(bytesNeeded);

    let value;
    do {
        window.crypto.getRandomValues(bytes);
        value = 0;
        // Multiply rather than shift: `<<` truncates to 32 bits and would corrupt
        // ranges needing more than 4 bytes.
        for (let i = 0; i < bytesNeeded; i++) {
            value = value * 256 + bytes[i];
        }
    } while (value >= limit);

    return min + (value % range);
};

/** Uniform index in [0, length). Returns 0 for an empty range. */
export const cryptoRandomIndex = (length) => (length <= 0 ? 0 : cryptoRandomInt(0, length - 1));

/** Uniform float in [min, max). */
export const cryptoRandomFloat = (min, max) => {
    const bytes = new Uint8Array(7);
    window.crypto.getRandomValues(bytes);

    // Build exactly 53 bits — the widest integer a double holds without loss.
    // 6 whole bytes (48 bits) plus the top 5 bits of the seventh.
    let value = 0;
    for (let i = 0; i < 6; i++) {
        value = value * 256 + bytes[i];
    }
    value = value * 32 + (bytes[6] >> 3);

    return min + (value / Math.pow(2, 53)) * (max - min);
};
